import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tooltip,
  Button,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Mail as MailIcon,
  Message as MessageIcon,
  Phone as PhoneIcon,
  Chat as ChatIcon,
  Timer as TimerIcon,
  CallSplit as CallSplitIcon,
  Label as LabelIcon,
  Flag as FlagIcon,
  AutoFixHigh as SparklesIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  Category as CategoryIcon,
  Http as HttpIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { useDrag } from 'react-dnd';

const stepTypes = {
  communication: [
    {
      type: 'email',
      name: 'Email',
      icon: <MailIcon />,
      description: 'Send an email using a template',
    },
    {
      type: 'sms',
      name: 'SMS',
      icon: <MessageIcon />,
      description: 'Send an SMS using a template',
    },
    {
      type: 'voice',
      name: 'Voice Call',
      icon: <PhoneIcon />,
      description: 'Make a voice call using a template',
    },
    {
      type: 'chat',
      name: 'Chat Message',
      icon: <ChatIcon />,
      description: 'Send a chat message using a template',
    },
  ],
  flowControl: [
    {
      type: 'wait_step',
      name: 'Wait Step',
      icon: <TimerIcon />,
      description: 'A step that represents a fixed waiting period',
    },
    {
      type: 'validation_step',
      name: 'Validation Step',
      icon: <CallSplitIcon />,
      description: 'A step that validates certain conditions before proceeding',
    },
  ],
  integration: [
    {
      type: 'webhook',
      name: 'Webhook',
      icon: <HttpIcon />,
      description: 'A step that triggers external API calls',
    },
  ],
  goals: [
    {
      type: 'goal',
      name: 'Goal',
      icon: <FlagIcon />,
      description: 'A step that represents a conversion or success point',
    },
    {
      type: 'end',
      name: 'End',
      icon: <StopIcon />,
      description: 'A step that marks the end of a journey path',
    },
  ],
};

const StepCategory = ({ title, children, isCollapsed }) => (
  <Box sx={{ mb: 3 }}>
    {!isCollapsed && (
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        {title}
      </Typography>
    )}
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {children}
    </Box>
  </Box>
);

const DraggableStep = ({ type, name, icon, description, isCollapsed }) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'JOURNEY_STEP',
    item: { type },
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (item, monitor) => {
      const dropResult = monitor.getDropResult();
      if (item && dropResult) {
        // The drop was handled by the drop target
        console.log(`Dropped ${item.type} step`);
      }
    },
  }));

  return (
    <Tooltip title={isCollapsed ? description : ''} placement="right">
      <Paper
        ref={drag}
        elevation={0}
        sx={{
          p: 1,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'move',
          border: 1,
          borderColor: 'divider',
          bgcolor: isDragging ? 'action.hover' : 'background.paper',
          opacity: isDragging ? 0.5 : 1,
          '&:hover': {
            bgcolor: 'action.hover',
          },
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          transition: 'all 0.2s ease',
          transform: isDragging ? 'scale(1.02)' : 'scale(1)',
        }}
      >
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1,
            bgcolor: 'primary.light',
            color: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        {!isCollapsed && (
          <Typography variant="body2" noWrap>
            {name}
          </Typography>
        )}
      </Paper>
    </Tooltip>
  );
};

const StepPalette = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <Box
      sx={{
        width: isCollapsed ? '60px' : '280px',
        borderRight: 1,
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        position: 'relative',
        transition: 'width 0.3s ease-in-out',
      }}
    >
      <IconButton
        onClick={() => setIsCollapsed(!isCollapsed)}
        sx={{
          position: 'absolute',
          right: -20,
          top: '50%',
          transform: 'translateY(-50%)',
          bgcolor: 'background.paper',
          border: 1,
          borderColor: 'divider',
          '&:hover': {
            bgcolor: 'action.hover',
          },
          zIndex: 1,
        }}
      >
        {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
      </IconButton>

      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        {!isCollapsed ? (
          <>
            <Typography variant="subtitle1" color="textPrimary" gutterBottom>
              Journey Steps
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Drag and drop to add steps
            </Typography>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CategoryIcon color="primary" />
          </Box>
        )}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 1.5 }}>
        <StepCategory title="Communication" isCollapsed={isCollapsed}>
          {stepTypes.communication.map((step) => (
            <DraggableStep 
              key={step.type} 
              {...step}
              isCollapsed={isCollapsed}
            />
          ))}
        </StepCategory>

        <StepCategory title="Flow Control" isCollapsed={isCollapsed}>
          {stepTypes.flowControl.map((step) => (
            <DraggableStep 
              key={step.type} 
              {...step}
              isCollapsed={isCollapsed}
            />
          ))}
        </StepCategory>

        <StepCategory title="Integration" isCollapsed={isCollapsed}>
          {stepTypes.integration.map((step) => (
            <DraggableStep 
              key={step.type} 
              {...step}
              isCollapsed={isCollapsed}
            />
          ))}
        </StepCategory>

        <StepCategory title="Goals" isCollapsed={isCollapsed}>
          {stepTypes.goals.map((step) => (
            <DraggableStep 
              key={step.type} 
              {...step}
              isCollapsed={isCollapsed}
            />
          ))}
        </StepCategory>
      </Box>

      <Divider />
      <Box sx={{ p: 1.5 }}>
        {!isCollapsed ? (
          <Button
            variant="outlined"
            fullWidth
            startIcon={<SparklesIcon />}
            size="small"
          >
            Generate with AI
          </Button>
        ) : (
          <Tooltip title="Generate with AI" placement="right">
            <IconButton
              sx={{
                width: '100%',
                height: 36,
                border: 1,
                borderColor: 'divider',
              }}
            >
              <SparklesIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default StepPalette; 